import React, { useEffect, useState } from 'react';
import { Slide } from 'pure-react-carousel';
import {
    Alert,
    Button,
    IconSpinner,
    ListingCardsCarousel,
    NamedLink,
    ResponsiveImage,
} from '../../../../components';
import { AVATAR_IMAGE_VARIANTS } from '../../../../components/Avatar/helpers';
import { getRecentTransactions } from '../../../../util/api';
import { getTimeDiffRepresentation } from '../../../../util/time';
import { checkIfListingIsAnonymous, checkIfUserIsAnonymous } from '../../../../util/anonym';
import { IconAnonymousL } from '../../../../icons';
import css from './SectionAcceptedTransactions.css';
import classNames from 'classnames';
import { createSlug } from '../../../../util/urlHelpers';
import config from '../../../../config';

const { userTypeHorseowner } = config;

const today = new Date();

const TransactionCard = ({ transaction, isDesktop, isLast }) => {
    const {
        createdAt,
        customer: {
            id: { uuid: customerId },
            attributes: {
                publicData: { userType: customerUserType },
            },
        },
        rider: {
            attributes: {
                firstName: riderName,
                profileImage,
                profile: { publicData: riderPD },
            },
        },
        horseowner: {
            attributes: { firstName: ownerName },
        },
    } = transaction;

    const { listing } = transaction;
    const {
        attributes: {
            title,
            publicData: { mainListingImage },
        },
    } = listing;
    const listingId = listing?.id?.uuid;

    const { days, hours, minutes } = getTimeDiffRepresentation(new Date(createdAt), today, {
        prefix: 'Vor',
        daysLongText: 'Tagen',
    });

    const { hasDefaultAvatar } = riderPD || {};

    const riderImageVariants = profileImage?.attributes?.variants || {};
    const hasRiderProfileImage = AVATAR_IMAGE_VARIANTS.some(v => riderImageVariants[v]);
    const riderPlaceholder = (riderName || '').split('')[0];
    const horsePlaceholder = (title || '').split('')[0];
    const isAnonymousListing = checkIfListingIsAnonymous(transaction.listing);
    const isAnonymousRider = !hasDefaultAvatar && checkIfUserIsAnonymous(transaction.rider);

    const customerIsHorseOwner = customerUserType === userTypeHorseowner;
    const linkName = customerIsHorseOwner ? 'ListingPage' : 'ProfilePage';

    const linkParams = customerIsHorseOwner
        ? { id: listingId, slug: createSlug(listing) }
        : { id: customerId, userType: customerUserType };

    const showDelimiter = Boolean(isDesktop && !isLast);

    return (
        <div className={css.cardWrapper}>
            <NamedLink isNotRouterLink name={linkName} params={linkParams}>
                <div className={css.cardContainer}>
                    <div className={css.cardImagesHolder}>
                        {hasRiderProfileImage ? (
                            <NamedLink isNotRouterLink name={linkName} params={linkParams}>
                                <div
                                    className={classNames(css.riderImageHolder, {
                                        [css.anonym]: Boolean(isAnonymousRider),
                                    })}
                                >
                                    <ResponsiveImage
                                        rootClassName={css.riderImage}
                                        alt={riderName}
                                        image={profileImage}
                                        variants={AVATAR_IMAGE_VARIANTS}
                                        sizes="96px"
                                    />
                                    {isAnonymousRider && <IconAnonymousL width="28" height="28" />}
                                </div>
                            </NamedLink>
                        ) : (
                            <div className={css.riderImagePlaceholder}>{riderPlaceholder}</div>
                        )}

                        {mainListingImage ? (
                            <NamedLink
                                isNotRouterLink
                                name="ListingPage"
                                params={{ id: listingId, slug: createSlug(listing) }}
                            >
                                <div
                                    className={classNames(css.horseImageHolder, {
                                        [css.anonym]: Boolean(isAnonymousListing),
                                    })}
                                >
                                    <img className={css.horseImage} src={mainListingImage} />
                                    {isAnonymousListing && (
                                        <IconAnonymousL width="28" height="28" />
                                    )}
                                </div>
                            </NamedLink>
                        ) : (
                            <div className={css.horseImagePlaceholder}>{horsePlaceholder}</div>
                        )}
                    </div>
                    <p className={css.cardMatchBadge}>Match</p>
                    <p className={css.cardMatchParties}>
                        {riderName} und {ownerName}
                    </p>
                    <p className={css.cardMatchDate}> {days || hours || minutes || ''}</p>
                </div>
            </NamedLink>
            {showDelimiter && <div className={css.delimiter} />}
        </div>
    );
};

const SectionContent = ({
    requestRecentTransactions,
    nextRequestAllowed,
    isMobile,
    isTablet,
    transactions,
    loading,
}) => {
    const [disableAnimation, setDisableAnimation] = useState(false);

    useEffect(() => {
        if (loading) return setDisableAnimation(true);
        if (!loading) {
            // disable animation of the carousel-lib to prevent jumping
            // when new transactions are appended
            const id = requestAnimationFrame(() =>
                requestAnimationFrame(() => setDisableAnimation(false))
            );
            return () => cancelAnimationFrame(id);
        }
    }, [loading]);

    const visibleSlidesNum = isMobile ? 1 : isTablet ? 2 : 4;

    const notifyOnSlideChange = currentSlide => {
        if (!nextRequestAllowed) return;
        const visibleSlides = currentSlide + visibleSlidesNum;

        if (visibleSlides >= transactions.length) {
            requestRecentTransactions();
        }
    };

    return (
        <section
            className={classNames(css.root, {
                [css.singleItemRoot]: transactions && transactions.length === 1,
            })}
        >
            <div className={css.content}>
                <h2 className={css.heading}>Neue Matches</h2>
                <p>Akzeptierte Anfragen der letzten 30 Tage.</p>
            </div>
            <div className={css.sliderOuterContainer}>
                <ListingCardsCarousel
                    renderNextButton={
                        !loading
                            ? null
                            : ({ rootClassNames }) => (
                                  <Button
                                      className={classNames(rootClassNames, css.pseudoNextButton)}
                                  >
                                      <IconSpinner />
                                  </Button>
                              )
                    }
                    showPrevButton={!loading}
                    disableAnimation={disableAnimation}
                    showDots={visibleSlidesNum < 3}
                    sliderVisibleSlides={visibleSlidesNum}
                    sliderClassName={css.sliderOuterContainer}
                    naturalSlideWidth={284}
                    notifyOnSlideChange={notifyOnSlideChange}
                    listings={transactions}
                    isMobile={isMobile}
                    key={isMobile}
                    hideButtonsOnEndStart
                    minListingsCount={1}
                    rendererSingleItem={tx => <TransactionCard key="single" transaction={tx} />}
                    rendererCarousel={(tx, index) => (
                        <Slide index={index} key={index}>
                            <TransactionCard
                                transaction={tx}
                                isLast={index === transactions.length - 1}
                                isDesktop={Boolean(!isMobile && !isTablet)}
                            />
                        </Slide>
                    )}
                />
            </div>
        </section>
    );
};

const SectionAcceptedTransactions = ({ isMobile, isTablet, computing }) => {
    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState(null);
    const [lastTxTimestamp, setLastTxTimestamp] = useState(null);
    const [nextRequestAllowed, setNextRequestAllowed] = useState(true);

    const requestRecentTransactions = async () => {
        if (!nextRequestAllowed || loading) return;

        setLoading(true);

        try {
            const response = await getRecentTransactions(lastTxTimestamp || undefined);
            const { transactions } = await response.json();

            if (!Array.isArray(transactions) || transactions.length === 0) {
                return setNextRequestAllowed(false);
            }

            const lastTransaction = transactions[transactions.length - 1];

            setLastTxTimestamp(Number(new Date(lastTransaction.createdAt)));

            /** ProfilePage links need the customer's userType */
            const sorted = transactions
                .filter(tx => tx.customer?.attributes?.publicData?.userType)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setTransactions(prev => [...(prev || []), ...sorted]);
        } catch (e) {
            // do nothing
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        requestRecentTransactions();
    }, []);

    const dataAvailable = Array.isArray(transactions) && transactions.length > 0;

    if (computing || (!dataAvailable && loading)) {
        return <IconSpinner />;
    }

    return dataAvailable ? (
        <SectionContent
            transactions={transactions}
            isMobile={isMobile}
            isTablet={isTablet}
            loading={loading}
            nextRequestAllowed={nextRequestAllowed}
            requestRecentTransactions={requestRecentTransactions}
        />
    ) : null;
};

class SectionAcceptedTransactionsErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('SectionAcceptedTransactions crashed:', error, info && info.componentStack);
    }
    render() {
        if (this.state.error) {
            return (
                <Alert
                    type="failure"
                    rootClassName={css.errorAlert}
                    showAsPlainText
                    header="Neue Matches konnten nicht angezeigt werden"
                    description="Beim Laden der Matches ist ein Fehler aufgetreten. Bitte versuche es später erneut."
                    closeAllowed={false}
                />
            );
        }
        return this.props.children;
    }
}

export default props => (
    <SectionAcceptedTransactionsErrorBoundary>
        <SectionAcceptedTransactions {...props} />
    </SectionAcceptedTransactionsErrorBoundary>
);
